import { useDefaultFormErrorNotification } from '@/hooks';
import { Form, FormInstance } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  PropsWithChildren,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  CreateBankAccountFormValues,
  UpdateBankAccountRequest,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  useUpdateBankAccount,
  useUpdateStatusBankAccount,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import { BankAccountSettingsFormValues } from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.types';
import { removeMask } from '@/utils/mask';

type Props = PropsWithChildren<unknown>;

type Forms = {
  bankAccountGeneralForm?: CreateBankAccountFormValues;
  bankAccountConfigBullet?: BankAccountSettingsFormValues;
};

type InstanceForms = {
  bankAccountGeneralForm?: FormInstance<CreateBankAccountFormValues>;
  bankAccountConfigBullet?: FormInstance<BankAccountSettingsFormValues>;
};

type BankAccountContextProps = {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  contextForm: Forms;
  handleSubmit: () => void;
  handleActive: (active: boolean) => void;
  instanceForms: InstanceForms;
  setActive: (active: boolean) => void;
  active: boolean;
  disabled: boolean;
};

const BankAccountContext = createContext({} as BankAccountContextProps);

export const useBankAccountFormContext = () => useContext(BankAccountContext);

const BankAccountFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { bankAccountId } = useRouteMatch<{ bankAccountId: string }>().params;

  const [contextForm, setContextForm] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);

  const [instanceForms, setInstanceForms] = useState<InstanceForms>({});
  const [active, setActive] = useState(false);

  const updateMutation = useUpdateBankAccount();
  const updateStatusMutation = useUpdateStatusBankAccount();

  useEffect(() => {
    setContextForm({});
  }, [bankAccountId]);

  const disabled = !Object.keys(contextForm).some((key) => !!key);

  function handleFormChange(name: string, info: FormChangeInfo) {
    setInstanceForms((state) => ({ ...state, [name]: info.forms[name] }));
    if (name && info?.forms[name]) {
      setContextForm((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    try {
      setIsLoading(true);
      if (contextForm.bankAccountGeneralForm) {
        const { bank, ...values } = contextForm.bankAccountGeneralForm;

        const obj: UpdateBankAccountRequest = {
          ...values,
          bankId: bank.id,
          id: Number(bankAccountId),
          accountAddress: {
            ...values.accountAddress,
            zip: removeMask(values.accountAddress.zip),
          },
          active,
        };

        await updateMutation.mutateAsync(obj);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }
    setIsLoading(false);
    setContextForm({});
  }

  const handleActive = async (status: boolean) => {
    setIsLoading(true);

    await updateStatusMutation.mutateAsync(Number(bankAccountId));

    setIsLoading(false);

    setActive(status);
  };

  return (
    <BankAccountContext.Provider
      value={{
        disabled,
        active,
        setActive,
        contextForm,
        handleSubmit,
        handleActive,
        isLoading,
        setIsLoading,
        instanceForms,
      }}
    >
      <Form.Provider
        onFormChange={handleFormChange}
        onFormFinish={handleSubmit}
      >
        {children}
      </Form.Provider>
    </BankAccountContext.Provider>
  );
};

export default BankAccountFormProvider;
